import React, {useState, ReactNode, useCallback, useEffect} from 'react';
import * as sdk from 'matrix-js-sdk';
import {logger} from 'matrix-js-sdk/lib/logger';
import MatrixContext from './context';
import {store, useAppSelector} from '@/redux-store/store';
import {
  createAndInitializeClient,
  exchangeSsoToken,
  restartClient,
  startClientFromPersistedCredentials,
  stopClient
} from '@/services/matrix/client';
import {env} from '@/config';
import {useDispatch} from 'react-redux';
import {setMatrixCredentials} from '@/redux-store/slices/auth';
import {getJoinedRooms} from '@/services/matrix/room/get-joined-rooms';
import {
  extractJoinedRoomData,
  getMetadata,
  getOtherMembers,
  isNameChangeEvent,
  updateDirectChatData
} from '@/services/matrix/room';
import {
  removeRoomJoinedData,
  resetRoomsJoinedData,
  setDirectChatData,
  setRoomJoinedData
} from '@/redux-store/slices/matrix/room-data';
import {useTranslation} from 'react-i18next';
import {MatrixMembership} from '@/types/matrix';
import {DocumentType} from '@/types/common';
import {isNotExcludedRoom} from '@/services/matrix/utils/is-not-excluded-room';

const {PROTOCOL, HOST, MATRIX_SERVER_URL_SUFFIX, ENVIRONMENT} = env;
const MESSAGE_ORIGIN = `${PROTOCOL}://${HOST}`;

const MAX_JOIN_RETRIES = 5;

async function joinRoomWithRetry(
  client: sdk.MatrixClient,
  roomId: string,
  attempt = 1
): Promise<void> {
  try {
    console.log(`Attempt ${attempt}: joining room ${roomId}...`);
    await client.joinRoom(roomId);
    console.log(`Joined room ${roomId} successfully!`);
  } catch (err) {
    console.error(`Failed to join room ${roomId}`, err);

    if (attempt < MAX_JOIN_RETRIES) {
      const nextAttempt = attempt + 1;
      setTimeout(() => {
        joinRoomWithRetry(client, roomId, nextAttempt);
      }, 2000);
    } else {
      console.error(
        `Giving up joining room ${roomId} after ${attempt} attempts`
      );
    }
  }
}

export const MatrixProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const {t} = useTranslation();

  const [matrixClient, setMatrixClient] = useState<sdk.MatrixClient | null>(
    null
  );
  const [matrixUserId, setMatrixUserId] = useState<string | null>(null);
  const [unreadTotal, setUnreadTotal] = useState<number>(0);
  const [rooms, setRooms] = useState<sdk.Room[] | null>(null);

  const dispatch = useDispatch();

  const ssoToken = useAppSelector((state) => state.auth.keycloakUser?.token);

  const matrixCredentials = useAppSelector(
    (state) => state.auth.matrixCredentials
  );

  const handleStopMatrixClient = useCallback(async () => {
    if (matrixClient) {
      await stopClient(matrixClient);
      console.log('stopped matrix client');
      setMatrixClient(null);
    }
  }, [matrixClient]);

  const handleDisableLogsInProduction = useCallback(() => {
    if (ENVIRONMENT === 'production') {
      logger.disableAll();
    }
  }, []);

  const updateTokenAndRestartClient = useCallback(async () => {
    if (matrixClient && ssoToken) {
      const restartedClient = await restartClient(matrixClient, ssoToken);
      console.log('Client has been restarted: ', restartedClient);
      setMatrixClient(restartedClient);
    }
  }, [matrixClient, ssoToken]);

  const redirectToSSO = useCallback(() => {
    // const isSafari = isBrowserSafari();

    const redirectUrl = encodeURIComponent(
      window.location.origin + '/matrix/redirect'
    );
    const ssoUrl = `https://${MATRIX_SERVER_URL_SUFFIX}/_matrix/client/r0/login/sso/redirect?identity_provider=oidc-keycloak&redirectUrl=${redirectUrl}`;

    // TODO: Find a solution to handle login in safari (cookies are being blocked per default)!

    // if (isSafari) {
    //   console.log('is safari');
    //   window.location.href = ssoUrl;
    // } else {
    const iframe = document.getElementById(
      'matrix-link-handler'
    ) as HTMLIFrameElement;
    if (iframe) {
      iframe.src = ssoUrl;
    }
    // }
  }, []);

  const startMatrixLoginProcess = useCallback(async () => {
    logger.setLevel('warn');
    if (matrixClient) {
      console.log('matrix client already set, nod need to login');
      return;
    }

    if (matrixCredentials) {
      const newMatrixClient = await startClientFromPersistedCredentials(
        matrixCredentials
      );
      if (newMatrixClient) {
        setMatrixClient(newMatrixClient);
        setMatrixUserId(matrixCredentials.userId);
        handleDisableLogsInProduction();
      }
    } else {
      try {
        redirectToSSO();
      } catch (error) {
        console.error('Failed to exchange SSO token for Matrix token:', error);
      }
    }
  }, [
    handleDisableLogsInProduction,
    matrixClient,
    matrixCredentials,
    redirectToSSO
  ]);

  const loginAndInitializeToMatrix = useCallback(
    async (token: string) => {
      try {
        logger.setLevel('warn');
        if (!matrixClient) {
          const {accessToken, userId, deviceId} = await exchangeSsoToken(token);

          const newMatrixClient = await createAndInitializeClient(
            accessToken,
            userId
          );
          console.log('[loginAndInitializeToMatrix]: new matrix client: ', {
            newMatrixClient,
            userId
          });

          if (newMatrixClient) {
            setMatrixClient(newMatrixClient);
            setMatrixUserId(userId);
            dispatch(setMatrixCredentials({accessToken, userId, deviceId}));
            handleDisableLogsInProduction();
          }
        }
      } catch (error) {
        console.error(
          'Error during Matrix client initialization with token:',
          error
        );
      }
    },
    [dispatch, handleDisableLogsInProduction, matrixClient]
  );

  const getUnreadTotal = (rooms: sdk.Room[]): number => {
    let totalUnread = 0;
    rooms.forEach((r) => {
      totalUnread += r.getUnreadNotificationCount(
        sdk.NotificationCountType.Total
      );
    });
    return totalUnread;
  };

  const roomInviteListener = useCallback(
    (
      event: sdk.MatrixEvent,
      member: sdk.RoomMember,
      previousMembership?: string
    ) => {
      // console.log('room invite listener called');
      if (
        matrixClient &&
        member.membership === 'invite' &&
        member.userId === matrixUserId &&
        previousMembership !== 'join'
      ) {
        console.log('joining room: ', {
          matrixClient,
          mambership: member.membership,
          userId: member.userId,
          matrixUserId,
          previousMembership
        });
        joinRoomWithRetry(matrixClient, member.roomId);
        // matrixClient.joinRoom(member.roomId).then(() => {
        //   console.info(
        //     `Auto-joined ${member.roomId}, previous membership was ${previousMembership}`
        //   );
        // });
      } else {
        // console.log('could join using room invite listener: ', {
        //   matrixClient,
        //   member,
        //   matrixUserId,
        //   previousMembership,
        //   event
        // });
      }
    },
    [matrixClient, matrixUserId]
  );

  const roomTimelineListener = useCallback(
    async (event: sdk.MatrixEvent, eventRoom: sdk.Room | undefined) => {
      if (!matrixClient || !eventRoom) {
        return;
      }

      if (eventRoom.getMyMembership() !== 'join') {
        return;
      }

      if (isNameChangeEvent(event)) {
        return;
      }
      const metadata = getMetadata(eventRoom);
      if (!isNotExcludedRoom(metadata)) {
        return;
      }
      if (metadata?.documentType === DocumentType.P2P) {
        const otherUser = getOtherMembers(eventRoom)[0];
        if (otherUser) {
          updateDirectChatData(
            matrixClient,
            otherUser.userId,
            eventRoom.roomId
          );
        }
      }

      const roomJoinedData = await extractJoinedRoomData(eventRoom, t);

      if (eventRoom.getMyMembership() === 'join') {
        dispatch(setRoomJoinedData(roomJoinedData));
      }
    },
    [dispatch, matrixClient, t]
  );

  const roomReceiptListener = useCallback(
    async (event: sdk.MatrixEvent, room: sdk.Room) => {
      const membership = room.getMyMembership();

      if (!matrixClient || membership !== 'join') {
        return;
      }

      if (isNameChangeEvent(event)) {
        return;
      }
      const metadata = getMetadata(room);
      if (!isNotExcludedRoom(metadata)) {
        return;
      }

      const joinedRooms = getJoinedRooms(room.client);

      setUnreadTotal(getUnreadTotal(joinedRooms));
      const roomJoinedData = await extractJoinedRoomData(room, t);

      if (room.getMyMembership() === 'join') {
        dispatch(setRoomJoinedData(roomJoinedData));
      }
    },
    [dispatch, matrixClient, t]
  );

  const roomMyMembershipListener = useCallback(
    async (room: sdk.Room, membership: string, prevMembership?: string) => {
      const joinedRooms = getJoinedRooms(room.client);
      if (
        [
          MatrixMembership.JOIN,
          MatrixMembership.KNOCK,
          MatrixMembership.INVITE
        ].includes(membership as MatrixMembership)
      ) {
        if (
          !matrixClient ||
          (membership === MatrixMembership.JOIN &&
            prevMembership === MatrixMembership.JOIN)
        ) {
          return;
        }

        const roomJoinedData = await extractJoinedRoomData(room, t);
        dispatch(setRoomJoinedData(roomJoinedData));

        const roomIndex = joinedRooms.findIndex(
          (r) => r.roomId === room.roomId
        );
        if (roomIndex < 0) {
          joinedRooms.push(room);
        }
        setRooms(joinedRooms);
        setUnreadTotal(getUnreadTotal(joinedRooms));
      } else {
        dispatch(removeRoomJoinedData(room.roomId));
        const filteredRooms = joinedRooms.filter(
          (r) => r.roomId !== room.roomId
        );
        setRooms(filteredRooms);
        setUnreadTotal(getUnreadTotal(filteredRooms));
      }
    },
    [dispatch, matrixClient, t]
  );
  useEffect(() => {
    const handleTokenExchangeAndClientInitialization = async (
      token: string
    ) => {
      try {
        if (!matrixClient) {
          await loginAndInitializeToMatrix(token);
        }
      } catch (error) {
        console.error(
          'Error during Matrix client initialization with token:',
          error
        );
      }
    };

    const receiveMessage = (event: MessageEvent) => {
      if (event.origin === MESSAGE_ORIGIN && event.data.type === 'loginToken') {
        handleTokenExchangeAndClientInitialization(event.data.token);
      }
    };

    window.addEventListener('message', receiveMessage);

    return () => {
      window.removeEventListener('message', receiveMessage);
    };
  }, [loginAndInitializeToMatrix, matrixClient]);

  useEffect(() => {
    const errorHandler = (state: sdk.SyncState) => {
      console.log('matrix state: ', state);
      if (state === 'ERROR' || state === 'STOPPED') {
        console.log('Client has to be restarted');
        updateTokenAndRestartClient();
      }
    };

    matrixClient?.on(sdk.ClientEvent.Sync, errorHandler);

    return () => {
      matrixClient?.off(sdk.ClientEvent.Sync, errorHandler);
    };
  }, [matrixClient, updateTokenAndRestartClient]);

  useEffect(() => {
    if (matrixClient) {
      matrixClient.on(sdk.RoomEvent.MyMembership, roomMyMembershipListener);
      matrixClient.on(sdk.RoomEvent.Receipt, roomReceiptListener);
      matrixClient.on(sdk.RoomEvent.Timeline, roomTimelineListener);
      matrixClient.on(sdk.RoomMemberEvent.Membership, roomInviteListener);

      const myRooms = getJoinedRooms(matrixClient);
      if (myRooms) {
        setRooms(myRooms);
        setUnreadTotal(getUnreadTotal(myRooms));
        const roomsDataPromises = myRooms.map((room) => {
          return extractJoinedRoomData(room, t);
        });
        Promise.all(roomsDataPromises)
          .then((roomsJoinedData) => {
            dispatch(resetRoomsJoinedData(roomsJoinedData));
            const directMap =
              matrixClient
                .getAccountData('m.direct' as keyof sdk.AccountDataEvents)
                ?.getContent() || {};

            let directChatDataHasChanged = false;

            roomsJoinedData.forEach((joinedRoomData, index) => {
              if (joinedRoomData.isDirect) {
                const otherUser = getOtherMembers(myRooms[index])[0];
                if (
                  otherUser &&
                  (!directMap[otherUser.userId] ||
                    !directMap[otherUser.userId].includes(
                      joinedRoomData.roomId
                    ))
                ) {
                  updateDirectChatData(
                    matrixClient,
                    otherUser.userId,
                    joinedRoomData.roomId
                  );
                  directChatDataHasChanged = true;
                }
              }
            });

            if (!directChatDataHasChanged) {
              store.dispatch(setDirectChatData(directMap));
            }
          })
          .catch((error) => {
            console.error('Error fetching room data:', error);
          });
      }

      return () => {
        matrixClient.removeListener(sdk.RoomEvent.Receipt, roomReceiptListener);
        matrixClient.removeListener(
          sdk.RoomEvent.MyMembership,
          roomMyMembershipListener
        );
        matrixClient.removeListener(
          sdk.RoomEvent.Timeline,
          roomTimelineListener
        );
        matrixClient.removeListener(
          sdk.RoomMemberEvent.Membership,
          roomInviteListener
        );
      };
    }
  }, [
    dispatch,
    matrixClient,
    roomInviteListener,
    roomMyMembershipListener,
    roomReceiptListener,
    roomTimelineListener,
    t
  ]);

  return (
    <MatrixContext.Provider
      value={{
        matrixClient,
        handleStopMatrixClient,
        startMatrixLoginProcess,
        matrixUserId,
        unreadTotal,
        rooms
      }}
    >
      {children}
    </MatrixContext.Provider>
  );
};

export default MatrixProvider;
